/**
 * UI message catalogs and the translator (SPEC §2, plan §8.10, owner i18n-metadata).
 *
 * Every user-facing string goes through `t('area.key', vars)`. Catalogs are flat `key → message`
 * maps per locale. Messages use ICU MessageFormat (`{name}`, `{count, plural, one {# item} other
 * {# items}}`, `{kind, select, …}`); the web app compiles them with `intl-messageformat` at build
 * time and hands the compiled formatter in as `compile`. Without a compiler, `{name}` placeholders
 * are interpolated, which covers every message without plural or select.
 *
 * Metadata labels (objects, fields, options, stages …) are not catalog messages: they are
 * `LocalizedText`, resolved on the server for the member's language (`localized-text.ts`).
 */
export type Locale = 'en' | 'de';
/** Locales the product ships catalogs for; `en` is the fallback for every missing message. */
export const LOCALES: readonly Locale[] = ['en', 'de'];
export type Messages = Record<string, string>;
export type MessageCatalog = Partial<Record<Locale, Messages>>;

/** Values for a message's placeholders. Dates and numbers are formatted by the message. */
export type MessageValue = string | number | boolean | Date | null | undefined;
export type MessageVars = Record<string, MessageValue>;

export type Translator = (key: string, vars?: MessageVars) => string;

/** Formats one compiled message with its values. */
export type MessageFormatter = (vars?: MessageVars) => string;
/** Compiles a message of a locale (ICU MessageFormat); called once per message and locale. */
export type CompileMessage = (message: string, locale: Locale, key: string) => MessageFormatter;

/** `{name}` interpolation (no plural or select); unknown placeholders stay visible. */
export function interpolate(template: string, vars?: MessageVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const v = vars[name];
    return v === undefined || v === null ? match : v instanceof Date ? v.toISOString() : String(v);
  });
}

const simpleCompile: CompileMessage = (message) => (vars) => interpolate(message, vars);

export type TranslatorOptions = {
  /** Message compiler (ICU). Default: `{name}` interpolation. */
  compile?: CompileMessage;
  /** Called once per key that has no message in any catalog (development warnings). */
  onMissing?: (key: string, locale: Locale) => void;
};

/** Merge catalogs into one message map per locale (later catalogs win on duplicate keys). */
export function mergeCatalogs(catalogs: readonly MessageCatalog[]): Record<Locale, Messages> {
  const merged: Record<Locale, Messages> = { en: {}, de: {} };
  for (const c of catalogs) for (const l of LOCALES) if (c[l]) Object.assign(merged[l], c[l]);
  return merged;
}

/**
 * Translator for one locale: the locale's message, else the English one, else the key itself.
 * Compiled messages are cached per key.
 */
export function createTranslator(
  catalogs: readonly MessageCatalog[],
  locale: Locale,
  options: TranslatorOptions = {},
): Translator {
  const merged = mergeCatalogs(catalogs);
  const compile = options.compile ?? simpleCompile;
  const cache = new Map<string, MessageFormatter>();
  const missing = new Set<string>();
  return (key, vars) => {
    let format = cache.get(key);
    if (!format) {
      const own = merged[locale][key];
      const message = own ?? merged.en[key];
      if (message === undefined) {
        if (!missing.has(key)) {
          missing.add(key);
          options.onMissing?.(key, locale);
        }
        return key;
      }
      format = compile(message, own !== undefined ? locale : 'en', key);
      cache.set(key, format);
    }
    return format(vars);
  };
}

// -- labels inside sentences ----------------------------------------------------------------------

/**
 * A metadata label (object, field, option) placed inside a sentence: "New {object}". German keeps
 * nouns capitalised, so the label stays as written ("Neu: Kandidat"); English lower-cases plain
 * capitalised words ("New candidate", "Search close dates") but keeps acronyms and brand-like words
 * ("SLA policy", "LinkedIn profile"). Never call `toLowerCase()` on a label (plan §8.10); server
 * messages use this too.
 */
export function inSentence(label: string, locale: string): string {
  if (!/^en\b/.test(locale)) return label;
  return label.replace(/\p{L}[\p{L}\p{M}'’-]*/gu, (word) =>
    /^\p{Lu}[\p{Ll}\p{M}'’-]*$/u.test(word) ? word.charAt(0).toLowerCase() + word.slice(1) : word,
  );
}

// -- keyboard shortcuts -------------------------------------------------------------------------

export type ShortcutPlatform = 'mac' | 'other';

const MAC_KEYS: Record<string, string> = {
  mod: '⌘',
  meta: '⌘',
  cmd: '⌘',
  ctrl: '⌃',
  control: '⌃',
  alt: '⌥',
  option: '⌥',
  shift: '⇧',
  enter: '↵',
  return: '↵',
  backspace: '⌫',
  delete: '⌦',
  escape: 'Esc',
  esc: 'Esc',
  tab: '⇥',
  space: 'Space',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  pageup: 'PgUp',
  pagedown: 'PgDn',
  home: 'Home',
  end: 'End',
};

const OTHER_KEYS: Record<Locale, Record<string, string>> = {
  en: {
    mod: 'Ctrl',
    ctrl: 'Ctrl',
    control: 'Ctrl',
    meta: 'Win',
    cmd: 'Ctrl',
    alt: 'Alt',
    option: 'Alt',
    shift: 'Shift',
    enter: 'Enter',
    return: 'Enter',
    backspace: 'Backspace',
    delete: 'Del',
    escape: 'Esc',
    esc: 'Esc',
    tab: 'Tab',
    space: 'Space',
    pageup: 'PgUp',
    pagedown: 'PgDn',
    home: 'Home',
    end: 'End',
  },
  de: {
    mod: 'Strg',
    ctrl: 'Strg',
    control: 'Strg',
    meta: 'Win',
    cmd: 'Strg',
    alt: 'Alt',
    option: 'Alt',
    shift: 'Umschalt',
    enter: 'Eingabe',
    return: 'Eingabe',
    backspace: 'Rücktaste',
    delete: 'Entf',
    escape: 'Esc',
    esc: 'Esc',
    tab: 'Tab',
    space: 'Leertaste',
    pageup: 'Bild↑',
    pagedown: 'Bild↓',
    home: 'Pos1',
    end: 'Ende',
  },
};

const ARROWS: Record<string, string> = {
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
};

/** Modifier order on screen (Apple's order on macOS). */
const MAC_ORDER = ['ctrl', 'control', 'alt', 'option', 'shift', 'mod', 'meta', 'cmd'];
const OTHER_ORDER = ['mod', 'ctrl', 'control', 'cmd', 'meta', 'alt', 'option', 'shift'];

/**
 * The keys of a shortcut as shown on a platform, in display order: `shortcutKeys('mod+shift+k',
 * 'mac', 'en')` → `['⇧', '⌘', 'K']`; on Windows/Linux in German → `['Strg', 'Umschalt', 'K']`.
 * A sequence (`g h`) is returned with a `then` marker between the steps: `['G', 'then', 'H']`.
 * Bindings use tinykeys-style names (`mod`, `shift`, `alt`, `ctrl`, `$mod` is accepted too).
 */
export function shortcutKeys(binding: string, platform: ShortcutPlatform, locale: Locale): string[] {
  const steps = binding.trim().split(/\s+/);
  const out: string[] = [];
  steps.forEach((step, i) => {
    if (i > 0) out.push('then');
    const parts = step
      .split('+')
      .filter(Boolean)
      .map((p) => p.replace(/^\$/, '').toLowerCase());
    const order = platform === 'mac' ? MAC_ORDER : OTHER_ORDER;
    const mods = parts.filter((p) => order.includes(p)).sort((a, b) => order.indexOf(a) - order.indexOf(b));
    const keys = parts.filter((p) => !order.includes(p));
    for (const p of [...mods, ...keys]) {
      const named = platform === 'mac' ? MAC_KEYS[p] : (OTHER_KEYS[locale][p] ?? ARROWS[p]);
      out.push(named ?? (p.length === 1 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)));
    }
  });
  return out;
}

/**
 * A shortcut as one string: `⇧⌘K` on macOS, `Ctrl+Shift+K` / `Strg+Umschalt+K` elsewhere;
 * sequences read `G then H` / `G dann H`.
 */
export function formatShortcut(binding: string, platform: ShortcutPlatform, locale: Locale): string {
  const then = locale === 'de' ? ' dann ' : ' then ';
  const steps: string[][] = [[]];
  for (const k of shortcutKeys(binding, platform, locale)) {
    if (k === 'then') steps.push([]);
    else steps[steps.length - 1]!.push(k);
  }
  return steps.map((keys) => (platform === 'mac' ? keys.join('') : keys.join('+'))).join(then);
}
